/**
 * Design System Utilities
 * Helper functions to access CSS variables for dynamic theming
 */

/**
 * Get computed CSS variable value from the document root
 */
export function getCSSVariable(name: string, fallback = ''): string {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

/**
 * Get chart colors from CSS variables
 */
export function getChartColors(): string[] {
  if (typeof window === 'undefined') {
    return ['#E91E63', '#9C27B0', '#3F51B5', '#00BCD4', '#4CAF50', '#FF9800', '#F44336', '#8B5CF6'];
  }
  return [
    getCSSVariable('--color-chart-1', '#E91E63'),
    getCSSVariable('--color-chart-2', '#9C27B0'),
    getCSSVariable('--color-chart-3', '#3F51B5'),
    getCSSVariable('--color-chart-4', '#00BCD4'),
    getCSSVariable('--color-chart-5', '#4CAF50'),
    getCSSVariable('--color-chart-6', '#FF9800'),
    getCSSVariable('--color-chart-7', '#F44336'),
    getCSSVariable('--color-chart-8', '#8B5CF6'),
  ];
}

/**
 * Get rarity colors from CSS variables
 */
export function getRarityColor(rarity: string): { color: string; bg: string; glow: string } {
  const rarityMap: Record<string, { color: string; bg: string; glow: string }> = {
    common: {
      color: getCSSVariable('--color-rarity-common', '#9CA3AF'),
      bg: getCSSVariable('--color-rarity-common-bg', 'rgba(156, 163, 175, 0.2)'),
      glow: getCSSVariable('--color-rarity-common-glow', 'rgba(156, 163, 175, 0.4)'),
    },
    uncommon: {
      color: getCSSVariable('--color-rarity-uncommon', '#3B82F6'),
      bg: getCSSVariable('--color-rarity-uncommon-bg', 'rgba(59, 130, 246, 0.2)'),
      glow: getCSSVariable('--color-rarity-uncommon-glow', 'rgba(59, 130, 246, 0.4)'),
    },
    rare: {
      color: getCSSVariable('--color-rarity-rare', '#8B5CF6'),
      bg: getCSSVariable('--color-rarity-rare-bg', 'rgba(139, 92, 246, 0.2)'),
      glow: getCSSVariable('--color-rarity-rare-glow', 'rgba(139, 92, 246, 0.4)'),
    },
    epic: {
      color: getCSSVariable('--color-rarity-epic', '#EC4899'),
      bg: getCSSVariable('--color-rarity-epic-bg', 'rgba(236, 72, 153, 0.2)'),
      glow: getCSSVariable('--color-rarity-epic-glow', 'rgba(236, 72, 153, 0.4)'),
    },
    legendary: {
      color: getCSSVariable('--color-rarity-legendary', '#F59E0B'),
      bg: getCSSVariable('--color-rarity-legendary-bg', 'rgba(245, 158, 11, 0.2)'),
      glow: getCSSVariable('--color-rarity-legendary-glow', 'rgba(245, 158, 11, 0.4)'),
    },
  };
  return rarityMap[rarity.toLowerCase()] || rarityMap.common;
}

/**
 * Get status color from CSS variables
 */
export function getStatusColor(status: 'success' | 'warning' | 'error' | 'info'): string {
  return getCSSVariable(`--color-${status}`, status === 'success' ? '#10B981' : status === 'warning' ? '#F59E0B' : status === 'error' ? '#EF4444' : '#3B82F6');
}

/**
 * Get swipe colors from CSS variables
 */
export function getSwipeColors(): { like: string; superlike: string; dislike: string } {
  return {
    like: getCSSVariable('--color-swipe-like', '#10B981'),
    superlike: getCSSVariable('--color-swipe-superlike', '#FBBF24'),
    dislike: getCSSVariable('--color-swipe-dislike', '#EF4444'),
  };
}
